import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import TextInput from 'src/components/UI/input/TextInput';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  TextField,
  Typography
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import RadioInput from 'src/components/UI/input/RadioInput';
import ColorPicker from 'src/components/UI/input/ColorPicker';
import { useDiseaseColourKey } from 'src/components/pedigree-chart/pedigree-tool/carrier-status/useDiseaseColourKey';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import SelectInput from 'src/components/UI/input/SelectInput';
import { CONSTANTS } from 'src/components/questionnaire/Constants';
import { calculateAgeOnDate, canCalculateAgeOnDate } from 'src/util/util';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import {
  codesOfOption,
  recordedAs,
  searchDiseases,
  storedDiseaseFor
} from 'src/util/diseaseSearch';
import DiagnosisOptionRow from 'src/components/settings/DiagnosisOptionRow';
import DiseaseCodeChoice from 'src/components/settings/DiseaseCodeChoice';
import Alert from '@mui/material/Alert';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import {
  leadingClassification,
  strictAcmgTier
} from 'src/util/variantPathogenicity';
import ClinicalNoteDeleteModal from './ClinicalNoteDeleteModal';
import {
  AFFECTED_STATUS,
  AFFECTED_STATUS_OPTIONS,
  TIER_TO_CLASSIFICATION,
  REPORTED_CLASSIFICATION_OPTIONS
} from './carrierStatusOptions';
import { isAnonymousOrLoading } from '../../../anonymousPedigrees';

const ButtonContainer = styled(Grid)({
  display: 'flex',
  gap: 10,
  justifyContent: 'center',
  marginTop: 5
});

const ProvenancePill = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 12,
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary
}));

// Reserved for source:"inferred" unconfirmed obligate (Clinical-Insights-gated,
// later phase). Defined but intentionally unused this phase.
// eslint-disable-next-line no-unused-vars
const SuggestedChip = styled('span')(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 12,
  border: `1px dashed ${theme.palette.text.disabled}`,
  color: theme.palette.text.secondary,
  '&::before': {
    content: '"\\2726 "'
  }
}));

// The person's other disease records are compared on code, not name: two codes
// for one condition (Marfan syndrome under OMIM and under ICD-10) are distinct
// records by design, and re-coding one onto the other's code is what must be
// blocked — it would leave the person with two identical records.
const sameCode = (a, b) => String(a ?? '') === String(b ?? '');

const EditDiagnoseModal = ({
  handleClose,
  onSubmit,
  formValues: initialFormValues,
  onDeleteDiagnose,
  isDisabled,
  patientDob,
  currentUserName,
  staffMemberNames = [],
  genotypes = [],
  siblingDiagnoses = [],
  onDirty
}) => {
  const t = useAutoTranslation();

  // Publishes the first user edit upward. The flag has to live in
  // DiagnosesMenu because the dialog's header X is rendered by CustomDialog
  // from there and never passes through this component — a guard held locally
  // would catch the Cancel button and miss the X, which is how most people
  // close this dialog. TGCLOUD-1823.
  const markDirty = () => {
    if (onDirty) onDirty();
  };

  const [formValues, setFormValues] = useState({
    ...initialFormValues,
    recordedBy: initialFormValues?.recordedBy || currentUserName || ''
  });
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // This modal was cloned from DiagnosesMenu with the banner JSX left behind,
  // so `isError` was set in the catch and read by nothing — a dead state, and
  // `no-unused-vars` could not see it because the success path also read it.
  // The modal could not report even the one failure mode its parent could
  // (TGCLOUD-1785).
  const [unavailable, setUnavailable] = useState(null);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = React.useState(false);
  const [codingChoice, setCodingChoice] = useState({
    open: false,
    option: null,
    selectedId: null
  });

  const { content } = useContext(AppContext);
  // TGCLOUD-1793. A disease pinned by the site colour key is drawn in the pinned
  // colour on every pedigree, so a per-family colour chosen here would be stored
  // and then never rendered — which reads as the save having failed. The control
  // is therefore read-only for a pinned disease, and stays live for an unpinned
  // one, where it remains the only way to set that family's colour.
  //
  // Returns {} whenever the feature is off, so this is inert until a group opts
  // in and costs no request.
  const colourKeyEnabled =
    content?.settings?.systemFeatures?.FEATURE_SITE_DISEASE_COLOUR_KEY === true;
  const diseaseColourKey = useDiseaseColourKey(colourKeyEnabled);
  // The pin for the disease being edited, if it has one. Matched on the stored
  // id exactly, as every other colour lookup in this product does.
  const pinnedColour = formValues?.id
    ? diseaseColourKey[String(formValues.id)]
    : undefined;
  // SnackContext defaults to null, and this modal is rendered in contexts
  // without a provider (e.g. its own RTL tests) — read it defensively so a
  // missing provider cannot crash the editor.
  const snackContext = useContext(SnackContext);
  const anonymous = isAnonymousOrLoading(content?.settings?.pedigreeSetting);
  const carrierStatusEnabled =
    content?.settings?.systemFeatures?.FEATURE_CARRIER_STATUS_IN_CONDITION ===
    true;

  // Flatten this person's genotype variants, keeping only P/LP ones — the
  // pedigree carrier-tested provenance links to a single P/LP variant. P/LP is
  // normalized-on-read here (display/link only; no variant entry this phase).
  const plpVariantOptions = useMemo(() => {
    if (!carrierStatusEnabled) return [];
    const out = [];
    for (const g of genotypes || []) {
      for (const v of g?.variants || []) {
        // No variantId → no usable variantRef, and undefined ids would conflate
        // options (undefined === undefined) in the picker. Exclude them.
        if (!v.variantId) continue;
        // An exact tier is required: the stored enum distinguishes pathogenic
        // from likely_pathogenic, so a compound classification
        // ("Pathogenic/Likely pathogenic") is deliberately not offered here
        // rather than being guessed into one of them.
        const tier = strictAcmgTier(leadingClassification(v)?.classification);
        if (tier !== 'Pathogenic' && tier !== 'Likely Pathogenic') continue;
        out.push({
          variantId: v.variantId,
          classification: TIER_TO_CLASSIFICATION[tier],
          label: `${v.gene || ''} ${
            v.variantP || v.variantC || v.variantId || ''
          }`.trim()
        });
      }
    }
    return out;
  }, [carrierStatusEnabled, genotypes]);

  const selectedTestedVariant = useMemo(
    () =>
      plpVariantOptions.find(
        (o) => o.variantId === formValues?.carrierBasis?.tested?.variantRef
      ) || null,
    [plpVariantOptions, formValues?.carrierBasis?.tested?.variantRef]
  );

  // A persisted tested link whose variant is no longer in the current P/LP set
  // (reclassified or deleted). The value is still stored — surface it read-only
  // rather than rendering a silently-blank field.
  const storedTested = formValues?.carrierBasis?.tested;
  const testedLinkStale = !!storedTested?.variantRef && !selectedTestedVariant;

  const handleAffectedStatusChange = (e) => {
    const value = e.target.value;
    markDirty();
    setFormValues((prev) => {
      const next = { ...prev, affectedStatus: value };
      // Clearing carrierBasis when status moves away from carrier keeps the
      // record self-consistent (basis is meaningful only for carriers).
      if (value !== AFFECTED_STATUS.carrier) next.carrierBasis = undefined;
      return next;
    });
  };

  const handleTestedSelect = (event, option) => {
    markDirty();
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (option) {
        carrierBasis.tested = {
          variantRef: option.variantId,
          classification: option.classification
        };
      } else {
        delete carrierBasis.tested;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  const handleObligateReasonChange = (e) => {
    const reason = e.target.value;
    markDirty();
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (reason && reason.trim()) {
        // Obligate is only recorded once a reason is given; manual entry is
        // confirmed by definition.
        carrierBasis.obligate = { reason, source: 'manual', confirmed: true };
      } else {
        delete carrierBasis.obligate;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  // Reported = carrier known from an external/untracked test or clinical
  // knowledge. Recorded only once a note is given; coexists with tested/obligate.
  const handleReportedNoteChange = (e) => {
    const note = e.target.value;
    markDirty();
    setFormValues((prev) => {
      const carrierBasis = { ...(prev.carrierBasis || {}) };
      if (note && note.trim()) {
        carrierBasis.reported = { ...(carrierBasis.reported || {}), note };
      } else {
        delete carrierBasis.reported;
      }
      return {
        ...prev,
        carrierBasis: Object.keys(carrierBasis).length
          ? carrierBasis
          : undefined
      };
    });
  };

  const handleReportedClassificationChange = (e) => {
    const value = e.target.value;
    markDirty();
    setFormValues((prev) => {
      // Classification is meaningful only alongside a reported note.
      if (!prev.carrierBasis?.reported) return prev;
      return {
        ...prev,
        carrierBasis: {
          ...prev.carrierBasis,
          reported: { ...prev.carrierBasis.reported, classification: value }
        }
      };
    });
  };

  // The age on the calendar day of the diagnosis date (calculateAgeOnDate).
  // `dateReported` is a `type: Date` column with several writers: this
  // modal's picker (a local-anchored instant), the inline capture ('YYYY-MM-DD',
  // cast to UTC midnight), the spreadsheet importers and demo seed (UTC
  // midnight) and the importers' / suggestions' server `new Date()` — so it is
  // read by toLocalDateFromPickerOrImport, and the date field shows that same
  // day. A date before the birth has no age: blank, where calculateAge's
  // Math.abs gave a positive one.
  const computeAge = useCallback(
    (dateReported) => calculateAgeOnDate(patientDob, dateReported),
    [patientDob]
  );
  // Both dates readable: a blank age then means "before the birth" and is
  // applied; otherwise the saved age is kept (the inline capture records an
  // age with no date).
  const ageApplies = useCallback(
    (dateReported) => canCalculateAgeOnDate(patientDob, dateReported),
    [patientDob]
  );

  useEffect(() => {
    setFormValues({
      ...initialFormValues,
      recordedBy: initialFormValues?.recordedBy || currentUserName || ''
    });
  }, [initialFormValues, currentUserName]);

  useEffect(() => {
    // Anonymous pedigrees: age is the directly-edited source of truth — never
    // let a leftover dob+dateReported pair recompute over a typed age.
    if (anonymous || !patientDob) return;
    // Only apply a date-derived age — never erase the saved age just because
    // dateReported is empty. The inline-capture path stores age without a
    // date, and we must preserve it on re-hydration. A date before the birth
    // does erase it: that record has no age.
    if (!ageApplies(formValues.dateReported)) return;
    const nextAge = computeAge(formValues.dateReported);
    setFormValues((prev) => {
      if (String(nextAge) === String(prev.age ?? '')) return prev;
      return { ...prev, age: String(nextAge) };
    });
  }, [anonymous, patientDob, computeAge, ageApplies, formValues.dateReported]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...formValues };
      // TGCLOUD-1766: the Desktop private flag is retired. A legacy value on the
      // stored diagnosis is not echoed back, so Cloud neither sets nor re-sends it.
      delete payload.isPrivate;
      await onSubmit(payload);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await onDeleteDiagnose(formValues._id);
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    markDirty();
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  // Re-point this record at a code. Only id+name are written: the search
  // option's own fields (codingSystems, sources, hasDuplicates) describe the
  // dropdown row, not the record, and spreading them would persist search
  // metadata onto the diagnosis. Colour is deliberately NOT set here — the
  // server re-derives it on a code change so the family keeps one colour per
  // disease and no colour shared by two (diagnoseController.updateDiagnoses).
  const applyDiseaseCode = (id, name) => {
    if (
      siblingDiagnoses.some(
        (d) => !sameCode(d._id, formValues?._id) && sameCode(d.id, id)
      )
    ) {
      snackContext?.setSnack?.({
        status: true,
        msg: t(
          "'{{name}}' with code {{id}} is already recorded for this person.",
          { name, id }
        ),
        severity: 'info'
      });
      return;
    }
    markDirty();
    setFormValues((pre) => ({ ...pre, id, name }));
  };

  const handleSelect = (event, value) => {
    if (!value) return;
    // Re-coding this record INTO a disease another of the person's records
    // already holds, under any of the row's codes, makes a duplicate whichever
    // code is chosen (TGCLOUD-1892). Moving the record to another code of its
    // own disease is what this dialog is for, so then only the chosen code is
    // checked, in applyDiseaseCode.
    const ownDisease = [...codesOfOption(value)].some((c) =>
      sameCode(c, formValues?.id)
    );
    const present =
      !ownDisease &&
      recordedAs(
        value,
        siblingDiagnoses.filter((d) => !sameCode(d._id, formValues?._id)),
        sameCode
      );
    if (present) {
      snackContext?.setSnack?.({
        status: true,
        msg: t("'{{name}}' is already recorded for this person.", {
          name: present.name || value.name
        }),
        severity: 'info'
      });
      return;
    }
    // More than one coding system for this name → let the clinician choose
    // which code to store, rather than silently taking the preferred one.
    // This is the path that makes re-coding possible at all: an ICD-10 record
    // can be moved to the same condition's OMIM code.
    if (value.codingSystems && value.codingSystems.length > 1) {
      setCodingChoice({
        open: true,
        option: value,
        selectedId: value.codingSystems[0].id
      });
      return;
    }
    const stored = storedDiseaseFor(value, value.id);
    applyDiseaseCode(stored.id, stored.name);
  };

  const closeCodingChoice = () =>
    setCodingChoice({ open: false, option: null, selectedId: null });

  const handleCodingChoiceConfirm = () => {
    const { option, selectedId } = codingChoice;
    closeCodingChoice();
    // The chosen code with its own name: Q99.2 is "Fragile X chromosome".
    if (option && selectedId) {
      const stored = storedDiseaseFor(option, selectedId);
      applyDiseaseCode(stored.id, stored.name);
    }
  };

  // One page of the disease search (util/diseaseSearch, TGCLOUD-1892); an
  // answer for words no longer in the box is dropped.
  const searchSeq = useRef(0);
  const fetchDataByDisease = async () => {
    const seq = ++searchSeq.current;
    if (searchTerm.length < 3) {
      setOptions([]);
      return;
    }
    const { options: found, unavailable: why } = await searchDiseases(
      searchTerm
    );
    if (seq !== searchSeq.current) return;
    setUnavailable(why);
    setOptions(found);
  };

  useEffect(() => {
    const handle = setTimeout(fetchDataByDisease, 250);
    return () => clearTimeout(handle);
    // eslint-disable-next-line
  }, [searchTerm]);

  const selectedItems = useMemo(() => {
    return formValues ? { id: formValues.id, name: formValues.name } : null;
  }, [formValues]);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  return (
    <form className="user-form" data-va-anchor="pedigree.diagnoses.editDialog">
      <ClinicalNoteDeleteModal
        loading={deleteLoading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={handleDelete}
        title="Are you sure you want to delete this disease ?"
        content={t(
          'Warning: This action will permanently delete this Disease. Once deleted, this information will be permanently removed and cannot be restored.'
        )}
      />
      {codingChoice.open && (
        <CustomDialog
          title={'Choose coding system'}
          open={codingChoice.open}
          handleClose={closeCodingChoice}
          minHeight="auto"
          showActions={true}
          saveBtnLabel={'Use this code'}
          isParentHandlingLoading={true}
          onSave={handleCodingChoiceConfirm}
        >
          <DiseaseCodeChoice
            option={codingChoice.option}
            selectedId={codingChoice.selectedId}
            onChange={(selectedId) =>
              setCodingChoice((prev) => ({ ...prev, selectedId }))
            }
            currentId={formValues?.id}
            sameId={sameCode}
          />
        </CustomDialog>
      )}
      <Grid
        container
        spacing={3}
        sx={{
          justifyContent: 'center'
        }}
      >
        {unavailable && (
          <Grid size={12}>
            <Alert severity="warning">
              {t(unavailable.message)}
              {unavailable.sources
                ? ` (${unavailable.sources.join(', ')})`
                : ''}
            </Alert>
          </Grid>
        )}
        <Grid size={12}>
          <Autocomplete
            clearOnBlur={false}
            id="tags-outlined"
            options={options}
            value={selectedItems}
            getOptionLabel={(option) => option.name}
            // The value is rebuilt from formValues, so it is never the same
            // object as an option — compare on code instead.
            isOptionEqualToValue={(option, value) =>
              sameCode(option?.id, value?.id)
            }
            onChange={handleSelect}
            filterOptions={(options, state) => options}
            // The shared row, as the sidebar's disease search draws it.
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <DiagnosisOptionRow option={option} chooser />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('Search for Diseases')}
                placeholder={t('Search...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            )}
            slotProps={{
              listbox: {
                sx: {
                  maxHeight: '320px',
                  overflowY: 'auto'
                }
              }
            }}
          />
          {/* The code being edited. Two records can share a name and differ
              only by coding system, so without this the clinician cannot tell
              which of them this dialog is editing. */}
          {formValues?.id && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.75,
                flexWrap: 'wrap'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary'
                }}
              >
                {t('Code')}: {formValues.id}
              </Typography>
              {!sameCode(initialFormValues?.id, formValues.id) && (
                <Chip
                  label={`${t('was')} ${initialFormValues?.id}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}
            </Box>
          )}
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Pedigree Display Name"
            name="alias"
            placeholder={t('Enter display name')}
            value={formValues?.alias}
            onChange={handleChange}
          />
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}
          size={6}
        >
          <FormLabel>{t('Select Colour')}</FormLabel>
          {pinnedColour ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: pinnedColour,
                    flexShrink: 0
                  }}
                />
                <Typography
                  sx={{ fontFamily: 'monospace', letterSpacing: 0.5 }}
                >
                  {pinnedColour.toUpperCase()}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {t(
                  'Set for the whole group under Settings, Pedigree, Disease colours. Changing it here would not appear on any pedigree.'
                )}
              </Typography>
            </>
          ) : (
            <ColorPicker color={formValues?.color} onChange={handleChange} />
          )}
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Diagnosis Age"
            name="age"
            type="number"
            value={formValues?.age ?? ''}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND date reported both set.
            // Editable when either is missing so a known age can be
            // recorded directly. Anonymous mode is always direct entry.
            disabled={!anonymous && !!patientDob && !!formValues?.dateReported}
            onChange={
              !anonymous && patientDob && formValues?.dateReported
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      markDirty();
                      setFormValues((prev) => ({
                        ...prev,
                        age: val === '' ? '' : String(parsed)
                      }));
                    }
                  }
            }
          />
        </Grid>
        {!anonymous && (
          <Grid size={6}>
            <TextInput
              type="date"
              label="Diagnosis Date"
              name="dateReported"
              value={toLocalDateFromPickerOrImport(formValues?.dateReported)}
              onChange={(date) => {
                markDirty();
                setFormValues((prev) => ({
                  ...prev,
                  dateReported: date,
                  age: ageApplies(date) ? String(computeAge(date)) : prev.age
                }));
              }}
              disableFuture
            />
          </Grid>
        )}
        <Grid size={12}>
          <TextInput
            label="Recorded By"
            name="recordedBy"
            type="autocomplete"
            value={formValues?.recordedBy || ''}
            options={staffMemberNames}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'recordedBy', value: label } });
            }}
          />
        </Grid>
        <Grid id="top" size={carrierStatusEnabled ? 6 : 12}>
          <FormControl component="fieldset" fullWidth>
            <Grid container spacing={1}>
              <Grid>
                <FormLabel>Mode of Inheritance </FormLabel>
              </Grid>
              <Grid size={12}>
                <SelectInput
                  fullWidth={true}
                  variant="outlined"
                  name="inheritanceMode"
                  options={CONSTANTS.inheritanceModesOptions}
                  value={formValues.inheritanceMode || ''}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </FormControl>
        </Grid>
        {carrierStatusEnabled && (
          <Grid size={6}>
            <FormControl component="fieldset" fullWidth>
              <Grid container spacing={1}>
                <Grid>
                  <FormLabel>{t('Affected Status')}</FormLabel>
                </Grid>
                <Grid size={12}>
                  <SelectInput
                    fullWidth={true}
                    variant="outlined"
                    name="affectedStatus"
                    options={AFFECTED_STATUS_OPTIONS}
                    value={formValues?.affectedStatus ?? ''}
                    onChange={handleAffectedStatusChange}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
        )}
        <Grid size={6}>
          <RadioInput
            label="Clinically Confirmed"
            name="clinicallyConfirmed"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.clinicallyConfirmed}
            row
          ></RadioInput>
        </Grid>
        <Grid size={6}>
          <RadioInput
            label="Patient Reported"
            name="isPatientReported"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.isPatientReported}
            row
          ></RadioInput>
        </Grid>
        <Grid size={6}>
          <RadioInput
            label="Display on Pedigree"
            name="displayOnPedigree"
            value={formValues?.displayOnPedigree}
            onChange={handleChange}
            options={yesNoOptions}
            row
          ></RadioInput>
        </Grid>

        {carrierStatusEnabled &&
          formValues?.affectedStatus === AFFECTED_STATUS.carrier && (
            <Grid size={12}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <FormLabel>{t('Tested')}</FormLabel>
                  {plpVariantOptions.length ? (
                    <Autocomplete
                      options={plpVariantOptions}
                      value={selectedTestedVariant}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, value) =>
                        option.variantId === value.variantId
                      }
                      onChange={handleTestedSelect}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('Pathogenic / likely-pathogenic variant')}
                          placeholder={t('Select a variant')}
                        />
                      )}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      {t('No pathogenic / likely-pathogenic variants recorded')}
                    </Typography>
                  )}
                  {testedLinkStale && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary'
                      }}
                    >
                      {t('Linked variant')}: {storedTested.variantRef}
                      {storedTested.classification
                        ? ` (${storedTested.classification})`
                        : ''}
                    </Typography>
                  )}
                </Grid>
                <Grid size={12}>
                  <FormLabel>
                    {t('Reported / clinically-known carrier')}
                  </FormLabel>
                  <TextInput
                    label="Reported carrier note"
                    name="reportedNote"
                    placeholder={t(
                      'e.g. carrier on external test (lab / date), not recorded in TrakGene'
                    )}
                    value={formValues?.carrierBasis?.reported?.note || ''}
                    onChange={handleReportedNoteChange}
                    required
                    multiline
                    rows={2}
                    fullWidth
                  />
                  {formValues?.carrierBasis?.reported && (
                    <>
                      <SelectInput
                        fullWidth
                        variant="outlined"
                        name="reportedClassification"
                        options={REPORTED_CLASSIFICATION_OPTIONS}
                        value={
                          formValues?.carrierBasis?.reported?.classification ||
                          ''
                        }
                        onChange={handleReportedClassificationChange}
                      />
                      <ProvenancePill>
                        {t('Reported · external/clinical')}
                      </ProvenancePill>
                    </>
                  )}
                </Grid>
                <Grid size={12}>
                  <TextInput
                    label="Obligate Reason"
                    name="obligateReason"
                    placeholder={t('Reason for obligate carrier designation')}
                    value={formValues?.carrierBasis?.obligate?.reason || ''}
                    onChange={handleObligateReasonChange}
                    required
                    multiline
                    rows={2}
                    fullWidth
                  />
                  {formValues?.carrierBasis?.obligate && (
                    <ProvenancePill>{t('Obligate · pedigree')}</ProvenancePill>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}

        <Grid size={6}>
          <TextInput
            label="Karyotype"
            name="karyotype"
            placeholder={t('e.g. 47,XX,+21 or 47,XX,+21[12]/46,XX[28]')}
            value={formValues?.karyotype || ''}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid size={6}>
          <RadioInput
            label="Mosaicism"
            name="mosaicism"
            onChange={handleChange}
            options={yesNoOptions}
            value={formValues?.mosaicism}
            row
          />
        </Grid>
        <Grid size={12}>
          <TextInput
            label="Comment"
            name="comment"
            placeholder={t('Enter comment')}
            value={formValues?.comment || ''}
            onChange={handleChange}
            multiline
            rows={3}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <ButtonContainer size={12}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOpenModal}
            disabled={isDisabled || deleteLoading}
            endIcon={deleteLoading ? <CircularProgress size={15} /> : undefined}
          >
            {t('Delete')}
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSave}
            disabled={isDisabled || loading || deleteLoading}
            endIcon={loading ? <CircularProgress size={15} /> : undefined}
          >
            {t('Save')}
          </Button>
        </ButtonContainer>
      </Grid>
    </form>
  );
};

export default EditDiagnoseModal;
